import type { BaseSyntheticEvent } from 'react'
import type { FieldValues, SubmitErrorHandler, SubmitHandler, UseFormReturn } from 'react-hook-form'

interface UseFormSaveOptions<TFieldValues extends FieldValues, TContext, TTransformedValues> {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>
  // Note: false for create, duplicate and first-time setup - those always validate
  isEdit?: boolean
  // Note: edits kept outside RHF (a file in useState, an open nested draft)
  hasExtraChanges?: boolean
  onUnchanged: () => void
  onSubmit: SubmitHandler<TTransformedValues>
  onInvalid?: SubmitErrorHandler<TFieldValues>
}

export const useFormSave = <
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  form,
  isEdit = true,
  hasExtraChanges = false,
  onUnchanged,
  onSubmit,
  onInvalid,
}: UseFormSaveOptions<TFieldValues, TContext, TTransformedValues>) => {
  // Note: read during render - RHF only keeps proxied formState fields up to date once subscribed
  const { isDirty } = form.formState
  const isUnchanged = isEdit && !isDirty && !hasExtraChanges

  return (e?: BaseSyntheticEvent) => {
    if (isUnchanged) {
      e?.preventDefault?.()
      onUnchanged()
      return Promise.resolve()
    }

    return form.handleSubmit(onSubmit, onInvalid)(e)
  }
}
